import type { WalletRecipientView } from '@/api/address-book-bff/types';
import type { RecipientKind } from '@/shared/modules/wallet/send/recipient';
import type { SendStatus } from '@/shared/modules/wallet/send/send-state';
import { CryptoAsset } from '@/shared/modules/wallet/use-crypto-assets';

import { useSendConfirmation } from './use-send-confirmation';
import { useSendDraft } from './use-send-draft';
import { useSendSubmission } from './use-send-submission';

export interface UseSendFlowOptions {
  presentation: 'modal' | 'page';
  open: boolean;
  assets: CryptoAsset[];
  senderAddress: string | undefined;
  initialCoin?: CryptoAsset;
  onClose: () => void;
}

export interface ConfirmationRecipient {
  address: string;
  identity: WalletRecipientView | null;
}

export interface SendFlowViewModel {
  coin: CryptoAsset | undefined;
  amount: string;
  recipientInput: string;
  selectedRecipient: WalletRecipientView | null;
  recipientTouched: boolean;
  kind: RecipientKind;
  recipientAddress: string | undefined;
  invalidRecipient: boolean;
  recipientHelper: string;
  amountUsd: number | null;
  amountError: string;
  balanceLabel: string;
  disabledReason: string | null;
  canContinue: boolean;
  confirming: boolean;
  confirmationRecipient: ConfirmationRecipient | null;
  status: SendStatus;
  error: string | null;
  digest: string | null;
  isBusy: boolean;
  requiresReauthentication: boolean;
  setAmount: (_amount: string) => void;
  setCoinType: (_coinType: string) => void;
  setRecipientInput: (_recipientInput: string) => void;
  resolveRecipientAddress: (_address: string) => void;
  selectRecipient: (_recipient: WalletRecipientView) => void;
  enrichRecipientIdentity: (_recipient: WalletRecipientView) => void;
  editRecipient: () => void;
  clearRecipient: () => void;
  handleRecipientBlur: () => void;
  startConfirmation: () => void;
  cancelConfirmation: () => void;
  handleVerificationCancel: () => void;
  handleSend: () => void;
  handleRetry: () => void;
  handleReauthenticate: () => Promise<void>;
  handleClose: () => void;
}

export const useSendFlow = ({
  open,
  assets,
  senderAddress,
  initialCoin,
  onClose,
}: UseSendFlowOptions): SendFlowViewModel => {
  const draft = useSendDraft({ open, assets, senderAddress, initialCoin });
  const submission = useSendSubmission();
  const confirmation = useSendConfirmation(submission);
  const { coin, canContinue, recipientAddress, selectedRecipient } = draft.view;

  const handleClose = () => {
    draft.reset();
    confirmation.cancel();
    submission.reset();
    onClose();
  };

  const startConfirmation = () => {
    if (canContinue) {
      confirmation.confirm(recipientAddress, selectedRecipient);
    }
  };

  return {
    ...draft.view,
    ...submission.state,
    confirming: confirmation.confirming,
    confirmationRecipient: confirmation.confirmationRecipient,
    startConfirmation,
    cancelConfirmation: confirmation.cancel,
    handleVerificationCancel: submission.cancelVerification,
    handleSend: () => confirmation.send(coin, draft.rawAmount),
    handleRetry: confirmation.retry,
    handleReauthenticate: submission.reauthenticate,
    handleClose,
  };
};
